import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';

const G = '#00C060';
const G_DARK = '#00A651';
const BG = '#f0f7f2';
const CARD = '#ffffff';
const BORDER = 'rgba(0, 192, 96, 0.4)';
const SHADOW = '0 2px 12px rgba(0,0,0,0.08)';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', color: '#e53935', bg: 'rgba(229,57,53,0.15)' },
  admin: { label: 'Admin', color: '#f57c00', bg: 'rgba(245,124,0,0.15)' },
  hotesse: { label: 'Hôtesse', color: G, bg: 'rgba(0,192,96,0.15)' },
};

const EMPTY_FORM = { username: '', password: '', full_name: '', email: '', role: 'hotesse', pdv_id: '' };

const inputStyle = { background: '#f8faf9', border: `1px solid ${BORDER}`, color: '#1a3322', borderRadius: '8px' };
const labelStyle = { color: '#1a3322', fontWeight: 600, fontSize: '13px', marginBottom: '6px' };

const Users = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadUsers = () =>
    api.get('/admin/users', { params: { brand: BRAND } }).then(r => setUsers(r.data));

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') { navigate('/dashboard'); return; }
    Promise.all([
      loadUsers(),
      api.get('/admin/pdv', { params: { brand: BRAND } }).then(r => setPdvs(r.data)),
    ])
      .catch(() => setMessage({ type: 'danger', text: 'Erreur lors du chargement' }))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name || undefined,
        email: formData.email || undefined,
        role: formData.role,
        pdv_id: formData.pdv_id ? parseInt(formData.pdv_id) : undefined,
      };
      await api.post('/admin/users', payload);
      setMessage({ type: 'success', text: 'Utilisateur créé avec succès' });
      setShowModal(false);
      setFormData(EMPTY_FORM);
      await loadUsers();
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.detail || 'Erreur lors de la création' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: BG }}>
      <div className="spinner-border" style={{ color: G }} role="status"></div>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ background: BG }}>
      <nav className="navbar navbar-dark sticky-top" style={{ background: '#0a1f0c', padding: '10px 0', borderBottom: `2px solid rgba(0,192,96,0.4)` }}>
        <div className="container d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <img src="/logo-white.png" alt="RNP" style={{ width: '46px', height: 'auto' }} />
            <div>
              <div className="fw-bold text-white" style={{ fontSize: '16px' }}>Reaktor Nature Power</div>
              <div style={{ fontSize: '11px', color: G }}>Utilisateurs</div>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="btn btn-sm"
            style={{ background: 'rgba(0,192,96,0.2)', border: `1px solid rgba(0,192,96,0.5)`, color: '#fff' }}>
            <i className="fas fa-arrow-left me-2"></i>Dashboard
          </button>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: '#1a3322', fontSize: '26px' }}>
              <i className="fas fa-user-cog me-2" style={{ color: G }}></i>Utilisateurs
            </h2>
            <p style={{ color: 'rgba(0,0,0,0.5)', margin: 0 }}>{users.length} utilisateur(s)</p>
          </div>
          <button onClick={() => { setFormData(EMPTY_FORM); setShowModal(true); }} className="btn btn-sm"
            style={{ background: G, color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px', padding: '8px 18px', borderRadius: '8px' }}>
            <i className="fas fa-plus me-2"></i>Créer un utilisateur
          </button>
        </div>

        <div className="row g-3 mb-4">
          {Object.entries(ROLE_CONFIG).map(([role, cfg]) => (
            <div key={role} className="col-md-4">
              <div style={{ background: CARD, borderRadius: '14px', padding: '20px', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
                <div style={{ width: '42px', height: '42px', background: cfg.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                  <i className="fas fa-user" style={{ color: cfg.color, fontSize: '18px' }}></i>
                </div>
                <div style={{ fontSize: '30px', fontWeight: 800, color: '#1a3322' }}>{users.filter(u => u.role === role).length}</div>
                <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.55)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cfg.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                  {['Utilisateur', 'Email', 'Rôle', 'PDV'].map(h => (
                    <th key={h} style={{ padding: '14px 16px', color: '#1a3322', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: h === 'Rôle' ? 'center' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan="4" style={{ padding: '50px', textAlign: 'center', color: 'rgba(0,0,0,0.35)' }}>
                    <i className="fas fa-users fa-3x d-block mb-3" style={{ opacity: 0.3, color: G }}></i>
                    Aucun utilisateur
                  </td></tr>
                ) : users.map((u, idx) => {
                  const roleCfg = ROLE_CONFIG[u.role] || { label: u.role, color: '#888', bg: 'rgba(0,0,0,0.08)' };
                  return (
                    <tr key={u.id} style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, background: idx % 2 === 0 ? '#fafffe' : '#fff' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div className="d-flex align-items-center gap-3">
                          <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${G}, ${G_DARK})`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '15px', flexShrink: 0 }}>
                            {u.username?.charAt(0)?.toUpperCase()}
                          </div>
                          <span style={{ color: '#1a3322', fontWeight: 600 }}>{u.username}</span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', color: '#555', fontSize: '14px' }}>{u.email || '-'}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, color: roleCfg.color, background: roleCfg.bg }}>
                          {roleCfg.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', color: '#555', fontSize: '14px' }}>{u.pdv_name || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '16px', border: `1px solid ${BORDER}` }}>
              <div className="modal-header" style={{ background: `linear-gradient(135deg, ${G}, ${G_DARK})`, borderRadius: '16px 16px 0 0', border: 'none' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#1a3322' }}>
                  <i className="fas fa-user-plus me-2"></i>Créer un utilisateur
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label style={labelStyle}>Nom d'utilisateur *</label>
                      <input className="form-control" style={inputStyle} required
                        value={formData.username}
                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                        placeholder="Ex: hotesse01" />
                    </div>
                    <div className="col-md-6">
                      <label style={labelStyle}>Mot de passe *</label>
                      <input className="form-control" style={inputStyle} type="password" required
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Mot de passe" />
                    </div>
                    <div className="col-md-6">
                      <label style={labelStyle}>Nom complet</label>
                      <input className="form-control" style={inputStyle}
                        value={formData.full_name}
                        onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                        placeholder="Ex: Marie Dupont" />
                    </div>
                    <div className="col-md-6">
                      <label style={labelStyle}>Email</label>
                      <input className="form-control" style={inputStyle} type="email"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@exemple.com" />
                    </div>
                    <div className="col-md-6">
                      <label style={labelStyle}>Rôle</label>
                      <select className="form-select" style={inputStyle}
                        value={formData.role}
                        onChange={e => setFormData({ ...formData, role: e.target.value })}>
                        <option value="hotesse">Hôtesse</option>
                        <option value="admin">Admin</option>
                        {user?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label style={labelStyle}>PDV</label>
                      <select className="form-select" style={inputStyle}
                        value={formData.pdv_id}
                        onChange={e => setFormData({ ...formData, pdv_id: e.target.value })}>
                        <option value="">Aucun PDV</option>
                        {pdvs.map(pdv => <option key={pdv.id} value={pdv.id}>{pdv.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{ border: 'none', padding: '16px 24px' }}>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn" disabled={saving}
                    style={{ background: G, color: '#fff', border: 'none', fontWeight: 700 }}>
                    {saving ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fas fa-check me-2"></i>}
                    Créer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
